import React, { useState } from "react";
import { Eye, Megaphone, Info, CheckCircle2, AlertTriangle, Siren, Bell, Sparkles, BadgePercent } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BAR = { info: ["bg-blue-600", Info], success: ["bg-brand-emerald", CheckCircle2], warning: ["bg-amber-500", AlertTriangle], urgent: ["bg-red-600", Siren] };
const img = (p) => (p ? fileUrl(p) : "");

function BannerView({ d }) {
  const [bg, Icon] = BAR[d.style] || BAR.info;
  return <div className={`${bg} text-white px-4 py-2.5 flex items-center gap-2 text-sm`}><Icon className="w-4 h-4 shrink-0" /><span className="font-medium">{d.message || "Your banner message"}</span></div>;
}

function AdView({ d }) {
  return (
    <div className="p-5"><span className="text-xs font-semibold uppercase tracking-widest text-brand-terracotta flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" />Sponsored</span>
      <div className="mt-3 max-w-xs bg-card border border-brand-line rounded-2xl overflow-hidden">
        {d.image && <div className="aspect-video bg-secondary"><img src={img(d.image)} alt="" className="w-full h-full object-cover" /></div>}
        <div className="p-4"><p className="font-heading font-semibold text-brand-ink">{d.title || "Ad title"}</p>{d.description && <p className="text-sm text-brand-muted mt-1">{d.description}</p>}</div>
      </div>
    </div>
  );
}

function CarouselView({ d }) {
  return (
    <div className="relative aspect-[16/5] bg-brand-ink">
      {d.image ? <img src={img(d.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">Upload an image to preview</div>}
      {d.title && <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end"><h3 className="text-white font-heading font-bold text-lg p-4">{d.title}</h3></div>}
    </div>
  );
}

function CardView({ d }) {
  return (
    <div className="px-5 py-5 flex flex-col sm:flex-row items-center gap-4 text-white" style={{ backgroundColor: d.bg_color || "#1a5f5a" }}>
      {d.image && <img src={img(d.image)} alt="" className="w-full sm:w-36 h-24 object-cover rounded-xl" />}
      <div className="flex-1 text-center sm:text-left"><h3 className="font-heading font-bold text-xl">{d.heading || "Card heading"}</h3>{d.description && <p className="text-white/90 text-sm mt-1">{d.description}</p>}
        {d.cta_label && <span className="inline-block mt-3 bg-white text-brand-ink font-semibold text-sm px-4 py-1.5 rounded-full">{d.cta_label}</span>}</div>
    </div>
  );
}

function MessageView({ d }) {
  return <div className={`${d.variant === "marquee" ? "bg-brand-emerald" : "bg-brand-terracotta"} text-white px-4 py-2.5 flex items-center gap-2 text-sm`}><Megaphone className="w-4 h-4 shrink-0" /><span className="font-medium">{d.text || "Your message"}</span></div>;
}

function PushView({ d }) {
  return (
    <div className="p-5"><div className="max-w-sm bg-white rounded-2xl shadow-lg border border-brand-line p-3 flex gap-3">
      <div className="w-9 h-9 rounded-lg bg-brand-emerald flex items-center justify-center shrink-0"><Bell className="w-5 h-5 text-white" /></div>
      <div className="min-w-0"><p className="text-sm font-semibold text-brand-ink">{d.title || "My Property"}</p><p className="text-sm text-brand-muted">{d.body || "Your notification message"}</p></div>
    </div></div>
  );
}

function PromoView({ d }) {
  const off = d.discount_type === "flat" ? `₹${d.discount_value || 0} off` : `${d.discount_value || 0}% off`;
  return (
    <div className="p-5"><div className="max-w-sm border-2 border-dashed border-brand-terracotta rounded-2xl p-4 bg-brand-terracotta/5">
      <p className="text-xs uppercase tracking-widest text-brand-terracotta font-semibold flex items-center gap-1"><BadgePercent className="w-4 h-4" />Promo code</p>
      <p className="font-heading font-bold text-2xl text-brand-ink mt-1 tracking-wider">{(d.code || "CODE").toUpperCase()}</p>
      <p className="text-sm text-brand-ink mt-1">{off}{d.valid_until ? ` · valid till ${d.valid_until}` : ""}</p>
      {d.description && <p className="text-xs text-brand-muted mt-1">{d.description}</p>}
    </div></div>
  );
}

const toDT = (v, end) => (v && v.length === 10 ? `${v}T${end ? "23:59" : "00:00"}` : v || "");
const nowIST = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 16);
const fmt = (v) => (v ? toDT(v).replace("T", " ") : "…");

export const ScheduleFields = ({ start, end, onChange, prefix }) => (
  <div className="grid grid-cols-2 gap-3" data-testid={`${prefix}-schedule`}>
    <div><label className="text-sm font-medium">Goes live <span className="text-brand-muted text-xs">(optional)</span></label>
      <input type="datetime-local" value={toDT(start)} onChange={(e) => onChange({ start_date: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid={`${prefix}-start`} /></div>
    <div><label className="text-sm font-medium">Ends <span className="text-brand-muted text-xs">(optional)</span></label>
      <input type="datetime-local" value={toDT(end, true)} onChange={(e) => onChange({ end_date: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid={`${prefix}-end`} /></div>
    <p className="col-span-2 text-xs text-brand-muted -mt-1">India time (IST). It appears and disappears on its own — leave empty to show right away / keep showing.</p>
  </div>
);

export const ScheduleBadge = ({ start, end, testId }) => {
  if (!start && !end) { return null; }
  const now = nowIST();
  const s = toDT(start); const e = toDT(end, true);
  const state = s && now < s ? ["Scheduled", "bg-blue-100 text-blue-700"] : e && now > e ? ["Ended", "bg-brand-muted/15 text-brand-muted"] : ["Live window", "bg-brand-sale/10 text-brand-sale"];
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${state[1]}`} data-testid={testId}>{state[0]} · {fmt(start)} → {fmt(end)}</span>;
};

const VIEWS = { banner: BannerView, ad: AdView, carousel: CarouselView, card: CardView, message: MessageView, push: PushView, promo: PromoView };

export const PreviewButton = ({ kind, data, testId }) => {
  const [open, setOpen] = useState(false);
  const View = VIEWS[kind];
  return (
    <>
      <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid={testId || `preview-${kind}`}><Eye className="w-4 h-4 mr-1.5" />Preview</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid={`preview-dialog-${kind}`}>
          <DialogHeader><DialogTitle>Preview — how it will look on the website</DialogTitle></DialogHeader>
          <div className="rounded-xl border border-brand-line overflow-hidden bg-brand-page">
            <div className="flex items-center gap-1.5 px-3 py-2 bg-secondary border-b border-brand-line"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="w-2.5 h-2.5 rounded-full bg-green-400" /><span className="ml-2 text-xs text-brand-muted">mypropertyas.com</span></div>
            <View d={data} />
          </div>
          <p className="text-xs text-brand-muted">Nothing is published until you press Save / Create.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
